import React, { useState, useMemo } from 'react';
import { ChevronDown, Play, Eye, Heart, MessageCircle, Share2, Calendar, TrendingUp, X } from 'lucide-react';
import { formatFullNumber, formatPercent, formatDate, getPlatformColor } from '../utils/formatters';
import YouTubeEstimateTooltip from './YouTubeEstimateTooltip';
import { isYouTubeDataStale } from '../utils/youtubeCompliance';

// API base URL for thumbnail resolution
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper to resolve thumbnail URLs (local cached thumbnails start with /api/)
const resolveThumbnailUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    const base = window.location.hostname === 'localhost' ? API_BASE_URL : '';
    return `${base}${url}?v3`;
  }
  return url;
};

const ContentGridView = ({ content, reportId, onContentExcluded, excludedContentIds = [], sortBy: controlledSortBy, onSortChange }) => {
  // Use controlled sortBy if provided, otherwise use internal state
  const [internalSortBy, setInternalSortBy] = useState('views-high');
  const sortBy = controlledSortBy !== undefined ? controlledSortBy : internalSortBy;
  const setSortBy = onSortChange || setInternalSortBy;
  const [filterCreator, setFilterCreator] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [itemsToShow, setItemsToShow] = useState(10);
  const [removing, setRemoving] = useState(null);
  const [toast, setToast] = useState(null);

  // Get unique creators and platforms from content
  const creators = useMemo(() => {
    const unique = [...new Set(content.map(c => c.creator?.id))].filter(Boolean);
    return unique.map(id => content.find(c => c.creator?.id === id)?.creator).filter(Boolean);
  }, [content]);

  const platforms = useMemo(() => {
    return [...new Set(content.map(c => c.platform))].filter(Boolean).sort();
  }, [content]);

  // Handle content exclusion
  const handleExcludeContent = async (contentId) => {
    if (!reportId) {
      console.log('No reportId - exclusion disabled');
      return;
    }
    
    setRemoving(contentId);
    try {
      console.log(`Excluding content ${contentId} from report ${reportId}`);
      const response = await fetch(`${API_BASE_URL}/api/client-reports/${reportId}/exclude-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentId })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setToast({
          id: null,
          message: `Failed to remove content (${response.status})`,
          type: 'error'
        });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const data = await response.json();
      console.log('Success response:', data);
      
      // Show undo toast
      setToast({
        id: contentId,
        message: 'Content removed from report',
        action: 'Undo',
        onUndo: () => handleRestoreContent(contentId)
      });

      // Auto-hide toast after 5 seconds
      setTimeout(() => {
        setToast(null);
      }, 5000);

      // Notify parent component
      if (onContentExcluded) {
        console.log('Calling onContentExcluded with data:', data);
        onContentExcluded(data);
      }
    } catch (error) {
      console.error('Error excluding content:', error);
      setToast({
        id: null,
        message: 'Failed to remove content: ' + error.message,
        type: 'error'
      });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setRemoving(null);
    }
  };

  const handleRestoreContent = async (contentId) => {
    if (!reportId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/client-reports/${reportId}/restore-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentId })
      });

      if (response.ok) {
        const data = await response.json();
        setToast(null);
        
        // Notify parent component
        if (onContentExcluded) {
          onContentExcluded(data);
        }
      }
    } catch (error) {
      console.error('Error restoring content:', error);
    }
  };

  // Sort and filter content (exclude removed items from normal view)
  const filteredContent = useMemo(() => {
    let filtered = [...content].filter(c => !excludedContentIds.includes(c.id));

    // Apply creator filter
    if (filterCreator !== 'all') {
      filtered = filtered.filter(c => c.creator?.id === filterCreator);
    }

    // Apply platform filter
    if (filterPlatform !== 'all') {
      filtered = filtered.filter(c => c.platform === filterPlatform);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-newest':
          return new Date(b.postDate) - new Date(a.postDate);
        case 'date-oldest':
          return new Date(a.postDate) - new Date(b.postDate);
        case 'views-high':
          return (b.views || 0) - (a.views || 0);
        case 'views-low':
          return (a.views || 0) - (b.views || 0);
        case 'likes-high':
          return (b.likes || 0) - (a.likes || 0);
        case 'likes-low':
          return (a.likes || 0) - (b.likes || 0);
        case 'engagement-high':
          return (b.engagementRate || 0) - (a.engagementRate || 0);
        case 'engagement-low':
          return (a.engagementRate || 0) - (b.engagementRate || 0);
        case 'avgCcv-high':
          return (b.avgCcv || 0) - (a.avgCcv || 0);
        case 'avgCcv-low':
          return (a.avgCcv || 0) - (b.avgCcv || 0);
        case 'peakCcv-high':
          return (b.peakCcv || 0) - (a.peakCcv || 0);
        case 'watchHours-high':
          return (b.watchHours || 0) - (a.watchHours || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [content, sortBy, filterCreator, filterPlatform, excludedContentIds]);

  return (
    <div className="space-y-6">
      {/* Controls Bar */}
      <div className="rounded-lg p-4 space-y-4 bg-gray-900 border border-white shadow-lg">
        {/* Sorting and Filtering Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Sort Dropdown */}
          <div>
            <label className="block text-xs font-medium text-white mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm text-white focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(75,85,99,1)' }}
            >
              <optgroup label="Date">
                <option value="date-newest">Newest First</option>
                <option value="date-oldest">Oldest First</option>
              </optgroup>
              <optgroup label="Views">
                <option value="views-high">Highest Views</option>
                <option value="views-low">Lowest Views</option>
              </optgroup>
              <optgroup label="Engagement">
                <option value="likes-high">Most Likes</option>
                <option value="likes-low">Least Likes</option>
                <option value="engagement-high">Highest Engagement Rate</option>
                <option value="engagement-low">Lowest Engagement Rate</option>
              </optgroup>
            </select>
          </div>

          {/* Creator Filter */}
          <div>
            <label className="block text-xs font-medium text-white mb-2">Creator</label>
            <select
              value={filterCreator}
              onChange={(e) => setFilterCreator(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm text-white focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(75,85,99,1)' }}
            >
              <option value="all">All Creators</option>
              {creators.map(creator => (
                <option key={creator.id} value={creator.id}>
                  {creator.name}
                </option>
              ))}
            </select>
          </div>

          {/* Platform Filter */}
          <div>
            <label className="block text-xs font-medium text-white mb-2">Platform</label>
            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm text-white focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(75,85,99,1)' }}
            >
              <option value="all">All Platforms</option>
              {platforms.map(platform => (
                <option key={platform} value={platform}>
                  {{ youtube: 'YouTube', tiktok: 'TikTok', twitch: 'Twitch', kick: 'Kick', twitter: 'Twitter', instagram: 'Instagram' }[platform?.toLowerCase()] || platform}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="text-sm text-white">
          Showing <span className="font-medium text-white">{filteredContent.length}</span> of <span className="font-medium text-white">{content.length}</span> videos
        </div>
      </div>

      {/* Grid of Content Cards */}
      {filteredContent.length > 0 ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredContent.slice(0, itemsToShow).map(video => (
              <div
                key={video.id}
                className="rounded-lg overflow-hidden hover:shadow-xl transition-all duration-200 flex flex-col bg-gray-900 border border-white shadow-lg"
              >
                {/* Thumbnail Container */}
                <div className="relative bg-gray-900 aspect-video overflow-hidden group">
                  {video.thumbnailUrl ? (
                    <img
                      src={resolveThumbnailUrl(video.thumbnailUrl)}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                      <Play className="w-8 h-8 text-white" />
                    </div>
                  )}

                  {/* Platform Badge */}
                  <div className="absolute top-2 right-2">
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium text-white"
                      style={{
                        backgroundColor: getPlatformColor(video.platform) + 'dd'
                      }}
                    >
                      {{ youtube: 'YouTube', tiktok: 'TikTok', twitch: 'Twitch', kick: 'Kick', twitter: 'Twitter', instagram: 'Instagram' }[video.platform?.toLowerCase()] || video.platform}
                    </span>
                  </div>

                  {/* Creator Avatar (Top Left) */}
                  <div className="absolute top-2 left-2">
                    {video.creator?.profileImageUrl ? (
                      <img
                        src={video.creator.profileImageUrl}
                        alt={video.creator.name}
                        className="w-6 h-6 rounded-full border-2 border-white object-cover"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-sc-pink border-2 border-white flex items-center justify-center">
                        <span className="text-xs font-medium text-white">
                          {video.creator?.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Remove Button Overlay */}
                  {reportId && (
                    <button
                      onClick={() => handleExcludeContent(video.id)}
                      disabled={removing === video.id}
                      className="absolute top-2 bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center bg-black/50 hover:bg-red-600/75 disabled:bg-gray-600 rounded-lg"
                      title="Remove from report"
                    >
                      {removing === video.id ? (
                        <div className="animate-spin">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                        </div>
                      ) : (
                        <X className="w-6 h-6 text-white" />
                      )}
                    </button>
                  )}
                </div>

                {/* Content Details */}
                <div className="flex-1 p-3 flex flex-col space-y-2">
                  {/* Title */}
                  <div>
                    <h3 className="text-xs font-medium text-white line-clamp-2 hover:text-sc-pink">
                      {video.title || 'Untitled Video'}
                    </h3>
                    <p className="text-xs text-white mt-0.5">
                      {video.creator?.name || 'Unknown Creator'}
                    </p>
                  </div>

                  {/* Date */}
                  <div className="flex items-center space-x-1 text-xs text-white">
                    <Calendar className="w-2.5 h-2.5" />
                    <span>{formatDate(video.postDate, 'short')}</span>
                  </div>

                  {/* Metrics Grid */}
                  {isYouTubeDataStale(video) && (
                    <div className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 rounded px-2 py-1">
                      <span title="YouTube data >30 days old — refresh to update">&#9888; YouTube data may be outdated</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 pt-1 border-t border-white/10">
                    {/* Views */}
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-0.5">
                        <Eye className="w-2.5 h-2.5 text-sc-pink" />
                        <span className="text-xs font-medium text-white">Views</span>
                      </div>
                      <p className="text-xs font-semibold text-white">
                        {formatFullNumber(video.views)}
                      </p>
                    </div>

                    {/* Likes */}
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-0.5">
                        <Heart className="w-2.5 h-2.5 text-red-500" />
                        <span className="text-xs font-medium text-white">Likes</span>
                      </div>
                      <p className="text-xs font-semibold text-white">
                        {formatFullNumber(video.likes)}
                      </p>
                    </div>

                    {/* Comments */}
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-0.5">
                        <MessageCircle className="w-2.5 h-2.5 text-green-500" />
                        <span className="text-xs font-medium text-white">Comments</span>
                      </div>
                      <p className="text-xs font-semibold text-white">
                        {formatFullNumber(video.comments)}
                      </p>
                    </div>

                    {/* Engagement Rate */}
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-0.5">
                        <TrendingUp className="w-2.5 h-2.5 text-purple-500" />
                        <span className="text-xs font-medium text-white">Eng. Rate</span>
                        <YouTubeEstimateTooltip metric="engagement" />
                      </div>
                      <p className="text-xs font-semibold text-white">
                        {formatPercent(video.engagementRate)}
                      </p>
                    </div>
                  </div>

                  {/* Impressions (if available) */}
                  {video.impressions && (
                    <div className="pt-1 border-t border-white/10">
                  </div>
                )}
              </div>
            </div>
          ))}
          </div>

          {/* Load More Button */}
          {itemsToShow < filteredContent.length && (
            <button
              onClick={() => setItemsToShow(prev => Math.min(prev + 10, filteredContent.length))}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium text-white hover:bg-white/10 transition-colors border border-white"
            >
              Load {Math.min(10, filteredContent.length - itemsToShow)} More Videos
            </button>
          )}
        </div>
      ) : (
        <div className="border-2 border-dashed border-white rounded-lg p-12 text-center bg-gray-900/50">
          <Play className="w-12 h-12 text-white mx-auto mb-4" />
          <p className="text-white font-medium">No content matches your filters</p>
          <p className="text-white text-sm mt-1">Try adjusting your sorting or filter options</p>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-4 right-4 rounded-lg shadow-lg p-4 flex items-center gap-3 z-50 backdrop-blur-xl ${
          toast.type === 'error' ? 'border border-red-500/30' : ''
        }`} style={{ background: '#111827', border: '1px solid rgba(75,85,99,1)' }}>
          <span className={toast.type === 'error' ? 'text-red-400 text-sm' : 'text-white text-sm'}>
            {toast.message}
          </span>
          {toast.action && (
            <button
              onClick={toast.onUndo}
              className="px-3 py-1 text-sm font-medium text-sc-pink hover:text-sc-pink/80 bg-sc-pink/10 rounded hover:bg-sc-pink/20 transition-colors"
            >
              {toast.action}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentGridView;
